/** Подписи значений, которые Core отдаёт кодами. Неизвестный код показывается как есть. */

export const encryptionValues = ["ssl", "tls", "starttls"] as const;
export type Encryption = (typeof encryptionValues)[number];

export const encryptionLabels: Record<Encryption, string> = {
  ssl: "SSL/TLS (неявный)",
  tls: "TLS",
  starttls: "STARTTLS",
};

/** Порты SMTP, которые принимает Core. */
export const smtpPorts = [465, 587] as const;

export const failureStatusValues = ["open", "retrying", "resolved"] as const;
export type FailureStatus = (typeof failureStatusValues)[number];

const failureStatusLabels: Record<FailureStatus, string> = {
  open: "Открыта",
  retrying: "Повтор в очереди",
  resolved: "Разобрана",
};

const failureStageLabels: Record<string, string> = {
  discovery: "Поиск писем",
  import: "Загрузка письма",
};

const errorCodeLabels: Record<string, string> = {
  "mail.import_failed": "Не удалось загрузить письмо",
  "mailbox.sync_failed": "Сбой синхронизации ящика",
  "mailbox.authentication_failed": "Ящик отклонил учётные данные",
  "document_workflow.mailbox_unavailable": "Почтовый сервер недоступен",
};

const mailboxStatusLabels: Record<string, string> = {
  pending: "Ждёт проверки",
  healthy: "Работает",
  failed: "Ошибка",
  error: "Ошибка",
  disabled: "Отключён",
};

const syncStatusLabels: Record<string, string> = {
  idle: "Ожидает",
  queued: "В очереди",
  running: "Идёт",
  dispatching: "Передаёт письма в обработку",
  dispatched: "Письма переданы",
  completed: "Завершена",
  failed: "Ошибка",
};

export const resolutionValues = [
  "manual_fix",
  "duplicate",
  "not_required",
] as const;
export type Resolution = (typeof resolutionValues)[number];

export const resolutionLabels: Record<Resolution, string> = {
  manual_fix: "Исправлено вручную",
  duplicate: "Письмо уже загружено",
  not_required: "Действий не требуется",
};

export function failureStatusLabel(status: string): string {
  return failureStatusLabels[status as FailureStatus] ?? status;
}

export function failureStageLabel(stage: string): string {
  return failureStageLabels[stage] ?? stage;
}

export function errorCodeLabel(code: string): string {
  return errorCodeLabels[code] ?? code;
}

export function mailboxStatusLabel(status: string, enabled: boolean): string {
  if (!enabled) return mailboxStatusLabels.disabled ?? "Отключён";
  return mailboxStatusLabels[status] ?? status;
}

export function syncStatusLabel(status: string): string {
  return syncStatusLabels[status] ?? status;
}

/** Состояние, при котором синхронизация ещё идёт и состояние стоит перечитывать. */
export function isSyncActive(status: string): boolean {
  return (
    status === "queued" || status === "running" || status === "dispatching"
  );
}

/**
 * Повтор доступен, только если Core пометил ошибку как повторяемую и она ещё не
 * разобрана: ровно то же условие Core проверяет и на своей стороне (409).
 */
export function canRetryFailure(failure: {
  retryable: boolean;
  status: string;
}): boolean {
  return (
    failure.retryable &&
    (failure.status === "open" || failure.status === "retrying")
  );
}

export function canResolveFailure(failure: { status: string }): boolean {
  return failure.status !== "resolved";
}
